import { forOwn, isEmpty, noop } from 'lodash';
import Logger from '@sm360/logger-sentry';
import PropTypes from 'prop-types';
import React, { Component } from 'react';

import { ConfigsService, LocaleService, WindowProxy, ContentZonesService } from '@sm360/widget-commons-core-services';
import {
    Account,
    AccountService,
    AnalyticsService,
    VIRTUAL_PAGEVIEWS as PAGES,
    EVENTS,
    ProfileService,
    ShiftDigitalService,
} from '@sm360/widget-commons-general';
import { FIELD_LENGTH, Groups, InputText, ValidationService } from '@sm360/widget-commons-forms';
import { Quote, Vehicle } from '@sm360/widget-commons-vehicle';
import { MessageBox } from '@sm360/widget-commons-basic-components';

import CBBAutoComplete from 'trade-in/components/CBBAutoComplete/CBBAutoComplete.jsx';

import ShowroomCBBService from 'trade-in/services/ShowroomCBBService/ShowroomCBBService.js';
import TradeInVehicleMapperService from 'trade-in/services/TradeInVehicleMapperService/TradeInVehicleMapperService';
import TradeInModel from 'trade-in/model/TradeInModel.js';
import TradeInService from 'trade-in/services/TradeInService/TradeInService.js';
import SLIDER from 'trade-in/constants/slider.js';

import 'trade-in/components/VehicleForm/VehicleForm.scss';

const fieldValidations = {
    odometer: ValidationService.validateOdometer,
    vin: ValidationService.optional(ValidationService.validateVin),
    year: ValidationService.validateYear,
};

const czGroup = 'widget.tradein.teaser';

class VehicleForm extends Component {
    constructor(props) {
        super(props);

        const { tradeInVehicle } = props;

        if (tradeInVehicle && tradeInVehicle.estimation) {
            // set update estimation.amountWasUpdated = true otherwise it will create a new lead
            tradeInVehicle.estimation.amountWasUpdated = true;
        }

        this.state = {
            formValidation: {
                make: false,
                model: false,
                year: false,
                exteriorColor: false,
                odometer: false,
                trim: false,
                vin: true,
                identityGroup: false,
            },
            hasBeenSubmitted: false,
            autoCompleteValue: '',
            tradeInVehicle,
            tradeInId: null,
            tradeInLeadId: null,
            account: props.account,
            displaySubmitError: false,
            displayCheckInfoMessage: false,
            estimation: null,
            profile: {},
            isLoggedIn: false,
            displayNotFoundError: false,
        };

        this.dispatchTradeInCreatedEvent = this.dispatchTradeInCreatedEvent.bind(this);
        this.fetchEstimation = this.fetchEstimation.bind(this);
        this.fillAutoCompleteValueFromTradeInVehicle = this.fillAutoCompleteValueFromTradeInVehicle.bind(this);
        this.getIdentityForm = this.getIdentityForm.bind(this);
        this.getVehicleForm = this.getVehicleForm.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleTradeInChange = this.handleTradeInChange.bind(this);
        this.isFieldValid = this.isFieldValid.bind(this);
        this.onAutoCompleteSelect = this.onAutoCompleteSelect.bind(this);
        this.onIdentityGroupChange = this.onIdentityGroupChange.bind(this);
        this.sendTradeIn = this.sendTradeIn.bind(this);
        this.validateVehicleInformationForm = this.validateVehicleInformationForm.bind(this);

        this.logger = new Logger(this.constructor.displayName, ConfigsService.getLoggerContext());

        this.cbbAutoCompleteRef = null;
        this.sendingTradeIn = false;
    }

    componentDidMount() {
        if (AccountService.isAuthenticated() && this.props.autofillTradeInVehicleFromAccount) {
            this.getAccountTradeIn();
            ProfileService.getLoggedAccountProfile().then((profile) => {
                this.setState({ isLoggedIn: true, profile });
            });
        }
        this.fillAutoCompleteValueFromTradeInVehicle();
        if (!this.props.isOfficialTradeIn) {
            AnalyticsService.virtualPageView(PAGES.ESTIMATED_TRADEIN_VEHICLE());
        } else {
            AnalyticsService.virtualPageView(PAGES.OFFICIAL_TRADEIN_VEHICLE_FORM());
        }
    }

    componentWillReceiveProps(newProps) {
        if (newProps.tradeInVehicle !== this.state.tradeInVehicle) {
            this.fillAutoCompleteValueFromTradeInVehicle(newProps.tradeInVehicle);
        }
    }

    onIdentityGroupChange(groupResponse) {
        if (this.state.isLoggedIn) {
            if (this.state.profile.homePhone === '') {
                const updatedAccount = { ...this.state.account, phoneNumber: groupResponse.value.phoneNumber };
                this.setState(
                    {
                        account: updatedAccount,
                        formValidation: { ...this.state.formValidation, identityGroup: groupResponse.valid },
                    },
                    () => this.props.updateAccount(updatedAccount)
                );
            } else {
                this.setState({
                    formValidation: { ...this.state.formValidation, identityGroup: groupResponse.valid },
                });
            }
        } else {
            this.setState({
                account: groupResponse.value,
                formValidation: { ...this.state.formValidation, identityGroup: groupResponse.valid },
            });
        }
    }

    onAutoCompleteSelect(autoCompleteValue, selectedValue) {
        let tradeInVehicle = null;
        if (selectedValue) {
            tradeInVehicle = {
                ...selectedValue,
                exteriorColor: this.state.tradeInVehicle.exteriorColor,
                odometer: String(this.state.tradeInVehicle.odometer),
            };
        } else {
            tradeInVehicle = {
                exteriorColor: this.state.tradeInVehicle.exteriorColor,
                odometer: String(this.state.tradeInVehicle.odometer),
            };
        }

        this.setState({ autoCompleteValue, tradeInVehicle, estimation: null }, () => {
            this.props.onTradeInVehicleUpdated(tradeInVehicle);
        });
    }

    getModelTrimStyle(vehicleId, year, make, model, trim) {
        // vehicleInfo: string - combination of vehicle year, make, model and trim
        const vehicleInfo = `${year} ${make} ${model} ${trim}`;
        // Extract model trim style from vehicle id
        return vehicleId.replace(new RegExp(`.*${vehicleInfo}`), '').trim();
    }

    getAccountTradeIn() {
        TradeInService.fetchTradeIn().then((tradeIn) => {
            const {
                estimation,
                id,
                leadId,
                vehicleId,
                make,
                model,
                trim,
                year,
                kilometers,
                exteriorColor,
                vin,
            } = tradeIn;

            const modelTrimStyle = this.getModelTrimStyle(vehicleId, year, make, model, trim);
            const vehicle = {
                id: vehicleId,
                make,
                model,
                modelTrim: trim,
                modelTrimStyle: [modelTrimStyle],
                year,
                odometer: kilometers,
                bodyColour: {
                    colorCode: exteriorColor,
                },
                vin,
            };

            const tradeInVehicle = TradeInVehicleMapperService.vehicleToTradeInVehicle({ ...vehicle });

            this.setState(
                (oldState) => ({ ...oldState, tradeInVehicle, tradeInId: id, tradeInLeadId: leadId, estimation }),
                () => this.fillAutoCompleteValueFromTradeInVehicle(tradeInVehicle)
            );
        });
    }

    getVehicleDetailsFields(useCBB, isOfficial) {
        return useCBB && !isOfficial ? (
            <div className="trade-in-form--autocomplete" key="vehicle-form">
                <CBBAutoComplete
                    value={this.state.autoCompleteValue}
                    onValueSelected={this.onAutoCompleteSelect}
                    dataTestPrefix="trade-in.vehicle-form"
                    ref={(element) => {
                        this.cbbAutoCompleteRef = element;
                    }}
                />
            </div>
        ) : (
            <div key="vehicle-form">
                <InputText
                    label={LocaleService.t('TRADE_IN.YEAR')}
                    errorLabel={LocaleService.t('COMMONS_FORMS.ERRORS.YEAR')}
                    name="year"
                    type="number"
                    value={this.state.tradeInVehicle.year}
                    classNames={['styled-placeholder quarter']}
                    isValid={this.state.formValidation.year}
                    isRequired
                    valueChange={this.handleTradeInChange}
                    hasBeenSubmit={this.state.hasBeenSubmitted}
                    dataTest="trade-in.vehicle-form.year"
                    maxLength={FIELD_LENGTH.YEAR_MAX}
                />
                <InputText
                    label={LocaleService.t('TRADE_IN.MAKE')}
                    errorLabel={LocaleService.t('COMMONS_FORMS.ERRORS.MAKE')}
                    name="make"
                    type="text"
                    value={this.state.tradeInVehicle.make}
                    classNames={['styled-placeholder triple-quarter']}
                    isValid={this.state.formValidation.make}
                    isRequired
                    valueChange={this.handleTradeInChange}
                    hasBeenSubmit={this.state.hasBeenSubmitted}
                    dataTest="trade-in.vehicle-form.make"
                    maxLength={FIELD_LENGTH.MAKE_MAX}
                />
                <InputText
                    label={LocaleService.t('TRADE_IN.MODEL')}
                    errorLabel={LocaleService.t('COMMONS_FORMS.ERRORS.MODEL')}
                    name="model"
                    type="text"
                    value={this.state.tradeInVehicle.model}
                    classNames={['styled-placeholder half']}
                    isValid={this.state.formValidation.model}
                    isRequired
                    valueChange={this.handleTradeInChange}
                    hasBeenSubmit={this.state.hasBeenSubmitted}
                    dataTest="trade-in.vehicle-form.model"
                    maxLength={FIELD_LENGTH.MODEL_MAX}
                />
                <InputText
                    label={LocaleService.t('TRADE_IN.TRIM')}
                    errorLabel={LocaleService.t('COMMONS_FORMS.ERRORS.TRIM')}
                    name="trim"
                    type="text"
                    value={this.state.tradeInVehicle.trim}
                    classNames={['styled-placeholder half']}
                    isValid={this.state.formValidation.trim}
                    isRequired
                    valueChange={this.handleTradeInChange}
                    hasBeenSubmit={this.state.hasBeenSubmitted}
                    dataTest="trade-in.vehicle-form.trim"
                    maxLength={FIELD_LENGTH.TRIM_MAX}
                />
            </div>
        );
    }

    getVehicleForm() {
        const vehicleColorMileageFields = [
            <InputText
                key="exteriorColor"
                label={LocaleService.t('TRADE_IN.COLOR')}
                errorLabel={LocaleService.t('COMMONS_FORMS.ERRORS.COLOR')}
                name="exteriorColor"
                type="text"
                value={this.state.tradeInVehicle.exteriorColor}
                classNames={['styled-placeholder half']}
                isValid={this.state.formValidation.exteriorColor}
                isRequired
                valueChange={this.handleTradeInChange}
                hasBeenSubmit={this.state.hasBeenSubmitted}
                dataTest="trade-in.vehicle-form.exterior-color"
                maxLength={FIELD_LENGTH.COLOR_MAX}
            />,
            <InputText
                key="odometer"
                label={LocaleService.t('TRADE_IN.MILEAGE')}
                errorLabel={LocaleService.t('COMMONS_FORMS.ERRORS.MILLEAGE')}
                name="odometer"
                type="number"
                min={0}
                maxLength={FIELD_LENGTH.ODOMETER_MAX}
                value={this.state.tradeInVehicle.odometer}
                classNames={['styled-placeholder half']}
                isValid={this.state.formValidation.odometer}
                isRequired
                valueChange={this.handleTradeInChange}
                hasBeenSubmit={this.state.hasBeenSubmitted}
                dataTest="trade-in.vehicle-form.odometer"
            />,
        ];
        const vinField = (
            <InputText
                label={LocaleService.t('TRADE_IN.VIN')}
                errorLabel={LocaleService.t('COMMONS_FORMS.ERRORS.VIN')}
                name="vin"
                type="text"
                value={this.state.tradeInVehicle.vin}
                classNames={['styled-placeholder full']}
                isValid={this.state.formValidation.vin}
                valueChange={this.handleTradeInChange}
                hasBeenSubmit={this.state.hasBeenSubmitted}
                dataTest="trade-in.vehicle-form.vin"
                minLength={FIELD_LENGTH.VIN_MIN}
                maxLength={FIELD_LENGTH.VIN_MAX}
            />
        );

        const vehicleFormFields = [
            this.getVehicleDetailsFields(this.props.useCBB, this.props.isOfficialTradeIn),
            ...vehicleColorMileageFields,
        ];

        if (!this.props.useCBB || this.props.isOfficialTradeIn) {
            vehicleFormFields.push(vinField);
        }

        return vehicleFormFields;
    }

    getIdentityForm() {
        return (
            <Groups.IdentityGroup
                values={this.state.account}
                labels={{
                    firstname: LocaleService.t('TRADE_IN.CONTACT.FIRST_NAME'),
                    lastname: LocaleService.t('TRADE_IN.CONTACT.LAST_NAME'),
                    phoneNumber: LocaleService.t('TRADE_IN.CONTACT.PHONE_NUMBER'),
                    email: LocaleService.t('TRADE_IN.CONTACT.EMAIL'),
                }}
                onChange={this.onIdentityGroupChange}
                hasBeenSubmit={this.state.hasBeenSubmitted}
                dataTestPrefix="trade-in.vehicle-form"
                isRestrictUpdateInfo
            />
        );
    }

    fillAutoCompleteValueFromTradeInVehicle(updatedVehicle) {
        const tradeInVehicle = updatedVehicle || this.state.tradeInVehicle;

        if (!isEmpty(tradeInVehicle)) {
            this.setState(
                (oldState) => {
                    return {
                        ...oldState,
                        tradeInVehicle,
                        // TODO: Find a way to use either tradeInVehicle.id or tradeInVehicle.value
                        autoCompleteValue:
                            !tradeInVehicle.id || tradeInVehicle.id === 0 ? tradeInVehicle.value : tradeInVehicle.id,
                        displayCheckInfoMessage: true,
                    };
                },
                () => {
                    this.validateVehicleInformationForm(tradeInVehicle);
                    this.props.onTradeInVehicleUpdated(tradeInVehicle);
                }
            );
        }
    }

    validateVehicleInformationForm(vehicle) {
        const formValidation = { ...this.state.formValidation };
        forOwn(formValidation, (isValid, key) => {
            if (key !== 'identityGroup') {
                formValidation[key] = this.isFieldValid(vehicle[key], key);
            }
        });
        this.setState({ formValidation });
    }

    isFieldValid(value, name) {
        return fieldValidations[name] ? fieldValidations[name](value) : ValidationService.validateIsNotEmpty(value);
    }

    handleSubmit(event) {
        event.preventDefault();
    }

    handleTradeInChange(value, name) {
        const { tradeInVehicle } = this.state;
        tradeInVehicle[name] = ['year', 'odometer'].includes(name) ? parseInt(value, 10) : value;

        this.setState(
            {
                tradeInVehicle,
                formValidation: {
                    ...this.state.formValidation,
                    [name]: this.isFieldValid(value, name),
                },
                // Set estimation to null, once trade in vehicle information changed
                // Setting estimation to null to get new estimation
                estimation: null,
            },
            this.props.onTradeInVehicleUpdated(tradeInVehicle)
        );
    }

    isAutocompleteFormValid(state) {
        return Promise.resolve(this.cbbAutoCompleteRef ? this.cbbAutoCompleteRef.validateInput() : true).then(
            (isAutoCompleteValid) =>
                isAutoCompleteValid &&
                !!state.autoCompleteValue &&
                !!state.tradeInVehicle.exteriorColor &&
                !!state.tradeInVehicle.odometer &&
                !!state.formValidation.identityGroup
        );
    }

    sendTradeIn() {
        // Allow sending only one trade-in at a time. Flag is reset after trade-in has been sent or validation has failed.
        if (this.sendingTradeIn) {
            return Promise.resolve();
        }

        this.sendingTradeIn = true;

        return Promise.resolve(
            this.props.useCBB && !this.props.isOfficialTradeIn
                ? this.isAutocompleteFormValid(this.state)
                : ValidationService.validateForm(this.state.formValidation)
        )
            .then((isFormValid) => {
                if (!isFormValid) {
                    this.setState({
                        hasBeenSubmitted: true,
                    });
                    return false;
                }

                this.props.lockScreen();
                this.setState({ displaySubmitError: false });

                return new Promise((resolve, reject) => {
                    const { tradeInVehicle, estimation } = this.state;

                    if (!estimation) {
                        return this.fetchEstimation(tradeInVehicle)
                            .then((response) => {
                                const result = response || { amount: 0, high: 0, low: 0 };
                                this.setState({
                                    estimation: result,
                                });
                                // Send tradeIn event for official tradeIn later on all the forms complete
                                if (!this.props.isOfficialTradeIn) {
                                    ShiftDigitalService.updateTradeInEvent(
                                        this.props?.quote?.id,
                                        'drTradeInEval',
                                        this.props?.quote?.vehicle
                                    );
                                }
                                return resolve(result);
                            })
                            .catch((e) => {
                                this.setState({ displayNotFoundError: true });
                                this.logger.error('An error happened while Fetching estimation', e, tradeInVehicle);
                                reject(e);
                            });
                    }
                    return resolve(estimation);
                })
                    .then((estimation) => {
                        const { tradeInVehicle, tradeInId, tradeInLeadId, account } = this.state;

                        const tradeInToSave = new TradeInModel({
                            id: tradeInId,
                            leadId: tradeInLeadId,
                            tradeInVehicle,
                            estimation,
                            profile: account,
                        });
                        // if there is a tradein id then use the
                        return TradeInService.saveTradeIn(tradeInToSave, this.props.quote);
                    })
                    .then(() => {
                        this.dispatchTradeInCreatedEvent();
                        this.props.lockScreen();
                        this.setState({ displaySubmitError: false });

                        const { tradeInVehicle, estimation } = this.state;
                        const data = {
                            tradeInVehicle,
                            tradeInValues: estimation,
                        };
                        return this.props.onTradeInVehicleUpdated(tradeInVehicle, () => this.props.onFormSubmit(data));
                    })
                    .catch((e) => {
                        this.logger.error('TradeIn#handleFormSubmit Promise.all error', e);
                        this.props.lockScreen();

                        this.setState({ displaySubmitError: true });
                        return Promise.reject(e);
                    });
            })
            .finally(() => {
                this.sendingTradeIn = false;
            });
    }

    dispatchTradeInCreatedEvent() {
        const $window = WindowProxy.get();
        if ($window) {
            $window.document.dispatchEvent(new Event(EVENTS.TRADEIN_CREATED));
        }
    }

    fetchEstimation(vehicle) {
        if (!this.props.useCBB || this.props.isOfficialTradeIn) {
            return Promise.resolve(null);
        }

        return ShowroomCBBService.fetchEstimation(vehicle)
            .then(({ low, high, taxesFactor }) => {
                const averageValue = Math.round(low + (high - low) / 2);
                const roundedAverageValue = Math.floor(averageValue / SLIDER.STEP_VALUE) * SLIDER.STEP_VALUE;

                const estimation = {
                    amount: roundedAverageValue,
                    high,
                    low,
                    taxesFactor,
                };

                return Promise.resolve(estimation);
            })
            .catch((e) => {
                this.logger.error('TradeIn#fetchEstimation error', e, vehicle);
                return Promise.reject(e);
            });
    }

    render() {
        const descriptionImageContentZone = ContentZonesService.getContentZone(czGroup, 'description.image');
        const descriptionAltContentZone = ContentZonesService.getContentZone(czGroup, 'description.image.alt');

        let buttonLabel = 'TRADE_IN.GET_YOUR_VALUE';
        if (AccountService.isAuthenticated) {
            buttonLabel = this.props.isOfficialTradeIn ? 'TRADE_IN.GET_OFFICIAL_VALUE' : 'TRADE_IN.GET_ESTIMATE_VALUE';
        }

        return (
            <div className="vehicle-form">
                <div className="vehicle-form-title">
                    <p className="is-price">{LocaleService.t('TRADE_IN.TITLES.TOP_DOLLAR')}</p>
                    <h2 className="sr-subtitle-1" data-test="vehicle-form_title">
                        {this.props.isOfficialTradeIn
                            ? LocaleService.t('TRADE_IN.TITLES.GET_OFFICIAL')
                            : LocaleService.t('TRADE_IN.TITLES.GET_ESTIMATE')}
                    </h2>
                    {this.props.isOfficialTradeIn &&
                        this.state.displayCheckInfoMessage && (
                            <h3 className="sr-text">{LocaleService.t('TRADE_IN.TITLES.CHECK_INFORMATIONS')}</h3>
                        )}
                </div>
                <div className="vehicle-form-body">
                    <form className="trade-in-form" onSubmit={this.handleSubmit}>
                        <div className="trade-in-cta">
                            {this.state.displaySubmitError && (
                                <MessageBox
                                    level="error"
                                    message={
                                        this.state.displayNotFoundError
                                            ? LocaleService.t('TRADE_IN.VEHICLE_NOT_FOUND')
                                            : LocaleService.t('TRADE_IN.ERROR')
                                    }
                                />
                            )}
                        </div>
                        <section className="clearfix vehicle-form-body--vehicle-details">
                            <h3 className="sr-text">{LocaleService.t('TRADE_IN.FORM_TITLE')}</h3>

                            {this.getVehicleForm()}
                        </section>

                        <section className="clearfix">
                            <h3 className="sr-text">{LocaleService.t('TRADE_IN.CONTACT.TITLE')}</h3>

                            {this.getIdentityForm()}
                        </section>

                        <section className="clearfix" data-test="trade-in.vehicle-form.submit">
                            <div className="trade-in-cta">
                                <button className="sr-button-1 is-uppercase" type="submit" onClick={this.sendTradeIn}>
                                    {LocaleService.t(buttonLabel)}
                                </button>
                            </div>
                        </section>
                    </form>
                    {!this.props.isOfficialTradeIn &&
                        descriptionImageContentZone && (
                            <div className="trade-in-talktrack">
                                <img
                                    src={descriptionImageContentZone.getValue()}
                                    alt={descriptionAltContentZone.getValue()}
                                />
                            </div>
                        )}
                </div>
            </div>
        );
    }
}

VehicleForm.defaultProps = {
    autofillTradeInVehicleFromAccount: true,
    updateAccount: noop,
    quote: null,
    account: new Account(),
};

VehicleForm.propTypes = {
    autofillTradeInVehicleFromAccount: PropTypes.bool,
    lockScreen: PropTypes.func.isRequired,
    onFormSubmit: PropTypes.func.isRequired,
    onTradeInVehicleUpdated: PropTypes.func.isRequired,
    quote: PropTypes.instanceOf(Quote.QuoteModel),
    useCBB: PropTypes.bool.isRequired,
    isOfficialTradeIn: PropTypes.bool.isRequired,
    tradeInVehicle: PropTypes.instanceOf(Vehicle.TradeInVehicleModel).isRequired,
    updateAccount: PropTypes.func,
    account: PropTypes.instanceOf(Account),
};

VehicleForm.displayName = 'VehicleForm';

export default VehicleForm;
